import { cn } from "@/lib/utils";
import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

type Props = {
  children: React.ReactNode;
  className?: string;
  onDelete: () => void;
  onClick?: () => void;
};

const SwipeToDeleteItem: React.FC<Props> = ({ children, onDelete, className, onClick }) => {
  const [translateX, setTranslateX] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const [showDesktopDelete, setShowDesktopDelete] = useState(false);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHorizontalSwipe = useRef(false);
  const globalListenersAdded = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  const deleteThreshold = -100; // px for full delete
  const stickyThreshold = -50; // px for sticky delete button
  const deleteButtonWidth = 80; // px width of delete button

  // Detect if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isMobile) return; // Only handle drag on mobile
    
    dragging.current = true;
    isHorizontalSwipe.current = false;
    globalListenersAdded.current = false;
    
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    
    startX.current = clientX;
    startY.current = clientY;
  };

  const handleGlobalMove = (e: TouchEvent | MouseEvent) => {
    if (!dragging.current || !isMobile) return;

    const clientX = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    
    const deltaX = clientX - startX.current;
    const deltaY = clientY - startY.current;

    // Determine if this is a horizontal swipe
    if (!isHorizontalSwipe.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    // Only handle horizontal swipes to the left
    if (isHorizontalSwipe.current && deltaX < 0) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      setTranslateX(deltaX);
    } else if (isHorizontalSwipe.current && deltaX >= 0) {
      // Reset if swiping right
      setTranslateX(0);
      setIsSticky(false);
    }
  };

  const handleGlobalEnd = (e: TouchEvent | MouseEvent) => {
    if (!dragging.current || !isMobile) return;

    // Only prevent parent events if this was our horizontal swipe
    if (isHorizontalSwipe.current) {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
    }

    cleanupGlobalListeners();

    if (translateX < deleteThreshold) {
      // Full delete
      onDelete();
    } else if (translateX < stickyThreshold) {
      // Stick to show delete button
      setTranslateX(-deleteButtonWidth);
      setIsSticky(true);
    } else {
      // Reset to original position
      setTranslateX(0);
      setIsSticky(false);
    }

    dragging.current = false;
    isHorizontalSwipe.current = false;
  };

  const addGlobalListeners = () => {
    if (!globalListenersAdded.current && isMobile) {
      document.addEventListener('touchmove', handleGlobalMove, { passive: false });
      document.addEventListener('mousemove', handleGlobalMove);
      document.addEventListener('touchend', handleGlobalEnd, { passive: false });
      document.addEventListener('mouseup', handleGlobalEnd);
      globalListenersAdded.current = true;
    }
  };

  const cleanupGlobalListeners = () => {
    if (globalListenersAdded.current) {
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalEnd);
      document.removeEventListener('mouseup', handleGlobalEnd);
      globalListenersAdded.current = false;
    }
  };

  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!dragging.current || !isMobile) return;

    // Add global listeners on first move
    addGlobalListeners();

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - startX.current;
    const deltaY = clientY - startY.current;

    // Determine if this is a horizontal swipe
    if (!isHorizontalSwipe.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      isHorizontalSwipe.current = Math.abs(deltaX) > Math.abs(deltaY);
    }

    // Only handle and prevent events for horizontal swipes to the left
    if (isHorizontalSwipe.current && deltaX < 0) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      e.preventDefault();

      setTranslateX(deltaX);
    }
  };

  const handleEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (!dragging.current || !isMobile) return;

    // Only prevent parent events if this was our horizontal swipe
    if (isHorizontalSwipe.current) {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      e.preventDefault();
    }
    
    cleanupGlobalListeners();

    if (translateX < deleteThreshold) {
      // Full delete
      onDelete();
    } else if (translateX < stickyThreshold) {
      // Stick to show delete button
      setTranslateX(-deleteButtonWidth);
      setIsSticky(true);
    } else {
      // Reset to original position
      setTranslateX(0);
      setIsSticky(false);
    }

    dragging.current = false;
    isHorizontalSwipe.current = false;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isMobile) {
      // Mobile: Handle sticky behavior
      if (isSticky) {
        setTranslateX(0);
        setIsSticky(false);
        e.stopPropagation();
        return;
      }

      // Only trigger click if we didn't drag and it wasn't a horizontal swipe
      if (translateX === 0 && onClick && !isHorizontalSwipe.current) {
        onClick();
      }
      e.stopPropagation();
    } else {
      // Desktop: Simple click
      if (onClick) {
        onClick();
      }
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    e.preventDefault();
    onDelete();
  };

  // Desktop hover handlers
  const handleMouseEnter = () => {
    if (!isMobile) {
      setShowDesktopDelete(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setShowDesktopDelete(false);
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupGlobalListeners();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("relative overflow-hidden group", className)}
      onMouseDown={isMobile ? handleStart : undefined}
      onTouchStart={isMobile ? handleStart : undefined}
      onMouseMove={isMobile ? handleMove : undefined}
      onTouchMove={isMobile ? handleMove : undefined}
      onMouseUp={isMobile ? handleEnd : undefined}
      onTouchEnd={isMobile ? handleEnd : undefined}
      onMouseEnter={!isMobile ? handleMouseEnter : undefined}
      onMouseLeave={!isMobile ? handleMouseLeave : undefined}
      onClick={handleClick}
      style={isMobile ? { 
        userSelect: 'none',
        WebkitUserSelect: 'none',
        msUserSelect: 'none',
      } : {}}
    >
      {/* Desktop X Button - only show on desktop */}
      {!isMobile && (
        <button
          className={cn(
            "absolute top-2 right-2 z-20 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-200 text-sm font-bold",
            showDesktopDelete ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
          onClick={handleDeleteClick}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <X size={12} />
        </button>
      )}

      {/* Mobile Delete Button - only show on mobile */}
      {isMobile && (
        <div
          className="absolute right-0 top-0 h-full bg-red-500 text-white z-0 transition-all duration-200"
          style={{ 
            width: `${Math.max(deleteButtonWidth, Math.abs(translateX) * 1.5)}px`, // Grow 1.5x faster than swipe
            opacity: Math.abs(translateX) > 10 ? 1 : 0.7, // Show more clearly when swiping
          }}
        >
          <button
            className="absolute right-0 top-0 h-full w-20 flex items-center justify-center hover:bg-red-600"
            onClick={handleDeleteClick}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              e.preventDefault();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              e.preventDefault();
            }}
          >
            Delete
          </button>
        </div>
      )}

      {/* Content */}
      <div
        className={cn(
          "relative z-10 bg-background p-4",
          isMobile ? "ease-out" : ""
        )}
        style={isMobile ? { 
          transform: `translateX(${translateX}px)`,
          transition: dragging.current ? 'none' : 'transform 200ms ease-out',
        } : {}}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeToDeleteItem;